import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Spin, message, Tooltip, Modal, Checkbox, Button, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  RightOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  fetchClinicianDirectory,
  cliniciansForGroup,
  isFollowUpForGroup,
  addressForLocation,
} from "../../../../services/clinicianDirectory";
// import { hasSchedulerUrlForProvider } from "../../../../services/zoomSchedulerLinks"; // old static-table filter, superseded below
import { getPatientAppointmentList } from "../../../redux/patientSlice";
import { getPatientCredits } from "../../../redux/creditsSlice";
import { mergeAppointments } from "../../../../utils/normalizeAppointments";
import {
  PROVIDER_CATEGORIES,
  CARE_GUIDE_CATEGORY_ID,
  CREDIT_BEARING_CATEGORIES,
} from "../../../../utils/providerCategories";
import BookingModal from "./BookingModal";
import CardComponent from "../../../UI/CardComponent";
import SubscriptionBanner from "../../../../assets/images/book/book_img.svg";
import careGuideCardIcon from "../../../../assets/images/book/book_one.svg";
import careGuideCardIcon2 from "../../../../assets/images/book/book_two.svg";
import careGuideCardIcon3 from "../../../../assets/images/book/book_three.svg";
import careGuideCardIcon4 from "../../../../assets/images/book/book_four.svg";
import neutralDoctorAvatar from "../../../../assets/images/users/clinician_default_avatar.png";

import { getBaseUrl } from "../../../../utils/envAccess";
import "../../../../screens/Subscription/subscription.css";


const HIDDEN_CAREGIVER_USER_REFS = new Set([]);


const SARAH_HARPER_CARE_GUIDE = {
  userId: "sarah-harper-care-guide",
  userRef: "sarah-harper-care-guide",
  name: "Sarah Harper",
  email: "",
  roleName: "Care Guide",
  picture: null,
  bio: "",
  slots: [],
  initialBookingLink: "https://scheduler.zoom.us/sarah-harper-care-guide/care-guide-virtual",
  followUpBookingLink: "",
  hasSufficientPoints: true,
};

const GROUP_SORT_KEYS = [
  "care guide",
  "15 minute",
  "introduction",
  "clinician",
  "doula",
  "fertility",
  "nutrition",
];


const categoryById = Object.fromEntries(PROVIDER_CATEGORIES.map((c) => [c.id, c]));

const GROUP_THEMES = [
  {
    categoryId: CARE_GUIDE_CATEGORY_ID,
    background: "rgba(255, 165, 0, 0.2)",
    accent: "#EA580C",
    iconSrc: careGuideCardIcon,
    emoji: "✿",
    displayName: "Care Guide - Free 15 Minute Call",
    bulletPoints: [
      "Get clarity on next steps and available care options",
      "Receive help with booking or navigating the platform",
      "Ask questions about the process and what to expect",
    ],
    subtitle: null,
  },
  {
    categoryId: "clinician",
    background: "rgba(206, 242, 244, 0.5)",
    accent: "#2563EB",
    iconSrc: careGuideCardIcon2,
    subtitle: "Doctor, Pharmacist, and Nurse Practitioner",
  },
  {
    categoryId: "fertility-doula",
    background: "rgba(239, 208, 189, 0.5)",
    accent: "#7C3AED",
    iconSrc: careGuideCardIcon3,
    subtitle: "Fertility Support Practitioner and Fertility Educator",
  },
  {
    categoryId: "nutritionist",
    background: "rgba(0, 128, 0, 0.5)",
    accent: "#15803D",
    iconSrc: careGuideCardIcon4,
    subtitle: null,
  },
].map((theme) => ({
  ...theme,
  keys: categoryById[theme.categoryId].matchKeywords,
  creditKey: categoryById[theme.categoryId].creditKey,
}));

const DEFAULT_THEME = {
  background: "#F8FAFC",
  accent: "#335CAD",
  emoji: "◆",
  subtitle: "Book with this care team.",
};

function themeForGroupName(name) {
  const n = (name || "").toLowerCase();
  for (const t of GROUP_THEMES) {
    if (t.keys.some((k) => n.includes(k))) return t;
  }
  return DEFAULT_THEME;
}

function sortGroupsForDesign(groups) {
  if (!Array.isArray(groups)) return [];
  return [...groups].sort((a, b) => {
    const an = (a.groupName || "").toLowerCase();
    const bn = (b.groupName || "").toLowerCase();
    const score = (name) => {
      const i = GROUP_SORT_KEYS.findIndex((k) => name.includes(k));
      return i === -1 ? 100 : i;
    };
    return score(an) - score(bn);
  });
}

const themeById = (id) => GROUP_THEMES.find((t) => t.categoryId === id);
const CARE_GUIDE_THEME = themeById(CARE_GUIDE_CATEGORY_ID);
const CARE_GUIDE_THEME_KEYS = CARE_GUIDE_THEME.keys;
const DOULA_THEME = themeById("fertility-doula");

function caregiverListHasCareGuide(groups) {
  return (groups || []).some((g) => {
    const n = (g.groupName || "").toLowerCase();
    return CARE_GUIDE_THEME_KEYS.some((k) => n.includes(k));
  });
}

function resolveGender(data) {
  const raw = data?.gender || data?.Gender || data?.sex || data?.Sex || data?.patientGender;
  if (!raw || typeof raw !== "string") return null;
  return raw.toLowerCase().trim();
}


const hasBookingLink = (c) => !!(c.initialBookingLink || c.followUpBookingLink);

const BOOKING_INFO_DISMISSED_KEY = "myfertility_booking_duration_info_dismissed";

const Services = () => {
  const [caregiverGroups, setCaregiverGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [caregiverDetails, setCaregiverDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const auth = useSelector((state) => state.authentication?.userAuth);
  const token = auth?.obj?.token || auth?.token || "";
  const isMale = resolveGender(auth?.obj ?? auth) === "male";
  const userKey = auth?.obj?.id || auth?.id || auth?.obj?.userRef || auth?.userRef || "anon";
  const infoModalDismissedKey = `${BOOKING_INFO_DISMISSED_KEY}_${userKey}`;

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [pendingGroup, setPendingGroup] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const dispatch = useDispatch();
  const zoomAppointments = useSelector((state) => state.patient?.zoomAppointments);
  const legacyAppointments = useSelector((state) => state.patient?.upcomingPatientAppointment);
  const [bookingClinician, setBookingClinician] = useState(null);

  const credits = useSelector((state) => state.credits);

  useEffect(() => {
    dispatch(getPatientAppointmentList());
  }, [dispatch]);

  
  useEffect(() => {
    dispatch(getPatientCredits());
  }, [dispatch]);

  const pastAppointments = useMemo(
    () =>
      mergeAppointments({
        legacy: legacyAppointments,
        zoom: zoomAppointments,
        viewer: "patient",
      }),
    [legacyAppointments, zoomAppointments],
  );


  const isFollowUp = useMemo(() => {
    const isCareGuideGroup =
      selectedGroup?.__careGuidePlaceholder || themeForGroupName(selectedGroup?.groupName) === CARE_GUIDE_THEME;
    if (isCareGuideGroup) return false;
    return isFollowUpForGroup(pastAppointments, selectedGroup, caregiverDetails);
  }, [pastAppointments, selectedGroup, caregiverDetails]);

 
  const fetchCaregiverDetails = useCallback(
    async (group) => {
      setLoadingDetails(true);
      setSelectedGroup(group);
      try {
        // Care Guide is always exactly one hardcoded person, not pulled from the clinician directory.
        if (group.__careGuidePlaceholder) {
          setCaregiverDetails([SARAH_HARPER_CARE_GUIDE].filter(hasBookingLink));
          return;
        }
        const directory = await fetchClinicianDirectory(token);
        const forGroup = cliniciansForGroup(directory, group).filter(hasBookingLink);
        setCaregiverDetails(forGroup);
      } catch (error) {
        console.error("Error fetching caregiver details:", error);
        message.error("Failed to load caregiver details. Please try again later.");
        setCaregiverDetails([]);
      } finally {
        setLoadingDetails(false);
      }
    },
    [token],
  );

  const handleBooking = (group) => {
    const isCareGuide = group.__careGuidePlaceholder || themeForGroupName(group.groupName) === CARE_GUIDE_THEME;
    const alreadyDismissed = localStorage.getItem(infoModalDismissedKey) === "true";

    if (!isCareGuide && !alreadyDismissed) {
      setPendingGroup(group);
      setDontShowAgain(false);
      setInfoModalOpen(true);
      return;
    }

    fetchCaregiverDetails(group);
  };

  const handleContinueFromInfoModal = () => {
    if (dontShowAgain) {
      localStorage.setItem(infoModalDismissedKey, "true");
    }
    setInfoModalOpen(false);
    fetchCaregiverDetails(pendingGroup);
    setPendingGroup(null);
  };

  const handleBackToCategories = () => {
    setSelectedGroup(null);
  };

  const fetchCaregiverGroups = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/Patient/CareGiverGroup`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch caregiver groups");
      }

      const data = await response.json();
      setCaregiverGroups(data);
    } catch (error) {
      console.error("Error fetching caregiver groups:", error);
      message.error("Failed to load care providers. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCaregiverGroups();
  }, [fetchCaregiverGroups]);

  const visibleCaregiverDetails = caregiverDetails.filter((c) => {
    const ref = c?.userRef != null ? String(c.userRef).trim() : "";
    return !HIDDEN_CAREGIVER_USER_REFS.has(ref);
  });

  const orderedGroups = useMemo(() => {
    const sorted = sortGroupsForDesign(caregiverGroups).filter((g) => {
      if (!isMale) return true;
      return themeForGroupName(g.groupName) !== DOULA_THEME;
    });
    if (caregiverListHasCareGuide(sorted)) return sorted;
    return [
      {
        groupId: null,
        groupName: "Care Guide",
        __careGuidePlaceholder: true,
      },
      ...sorted,
    ];
  }, [caregiverGroups, isMale]);

  const selectedTheme = selectedGroup ? themeForGroupName(selectedGroup.groupName) : null;

  const isCareGuideSelected =
    !!selectedGroup?.__careGuidePlaceholder || selectedTheme === CARE_GUIDE_THEME;

  const selectedCreditKey = selectedGroup?.__careGuidePlaceholder
    ? undefined
    : selectedTheme?.creditKey;
  const selectedCredits = selectedCreditKey ? credits?.[selectedCreditKey] : null;
  const selectedCreditsRemaining = selectedCredits?.remaining ?? 0;

  const creditsKnown = !credits?.loading && !credits?.error;
  const bucketEmpty = !!selectedCreditKey && creditsKnown && selectedCreditsRemaining < 1;
  const hasNoCredits =
    creditsKnown &&
    CREDIT_BEARING_CATEGORIES.every((c) => (credits?.[c.creditKey]?.remaining ?? 0) < 1);
  const hasCreditFor = (caregiver) =>
    isCareGuideSelected || (caregiver?.hasSufficientPoints !== false && !bucketEmpty);

  const handleSelectCaregiver = (caregiver) => {
    if (!hasCreditFor(caregiver)) {
      message.warning("You have no credits left for this provider type. Purchase a plan to book.");
      return;
    }
    setBookingClinician(caregiver);
  };

  // Booking now always goes through the clinician's Zoom Scheduler link
  // rather than our own availability slots, so every clinician in the
  // category is shown - there's no in-house slot data left to filter by.
  const filteredCaregiverDetails = visibleCaregiverDetails;

  const selectedGroupTitle = selectedGroup
    ? selectedGroup.__careGuidePlaceholder
      ? "Care Guide"
      : (selectedGroup.groupName || "").toUpperCase()
    : "";

  const providerInfo =
    "Choose a provider type to see who is available and open their scheduling link. You can switch anytime.";

  const servicesHeroIntroStyle = {
    margin: "0 0 12px",
    color: "#262626",
    fontSize: 14,
    lineHeight: 1.55,
    fontFamily: '"Montserrat", sans-serif',
    maxWidth: 520,
  };

  const servicesHeroDividerStyle = {
    height: 1,
    width: "100%",
    margin: "0 0 36px",
    border: "none",
    background:
      "linear-gradient(90deg, rgba(242, 170, 147, 0) 0%, rgba(242, 170, 147, 0.5) 25%, rgb(242, 170, 147) 50%, rgba(242, 170, 147, 0.5) 75%, rgba(242, 170, 147, 0) 100%)",
  };

  return (
    <div style={{ margin: "0 auto", padding: "8px 0 40px" }}>
      <Row gutter={[40, 32]} align="middle">
        <Col xs={24} lg={14}>
          <h1
            style={{
              color: "#335CAD",
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1,
              letterSpacing: "0.05em",
              margin: "0 0 20px 0",
            }}
          >
            BOOK YOUR NEXT APPOINTMENT
          </h1>
          <p style={servicesHeroIntroStyle}>
            Schedule time with our care providers for support tailored to your needs, preferences, and goals.
          </p>
          <p style={{ ...servicesHeroIntroStyle, margin: 0 }}>
            Our team is here to guide you every step of the way.
          </p>
        </Col>
        <Col xs={24} lg={10}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 140,
            }}
          >
            <img
              src={SubscriptionBanner}
              alt=""
              style={{
                width: "100%",
                maxWidth: 260,
                height: "auto",
                objectFit: "contain",
              }}
            />
          </div>
        </Col>
      </Row>
      <div role="presentation" style={servicesHeroDividerStyle} />

      {!selectedGroup && hasNoCredits && (
        <CardComponent
          borderWidth={1}
          borderColor="#FFCCC7"
          headerColor="rgba(255, 0, 0, 0.2)"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-start" }}>
              <CloseCircleFilled style={{ color: "#FF4D4F", fontSize: 22 }} aria-hidden />
              <span style={{ fontWeight: 700, color: "#434343", fontSize: 15 }}>
                You don&apos;t have any appointments available
              </span>
            </div>
          }
          headerStyle={{ borderBottom: "1px solid #FFCCC7", textAlign: "left" }}
          bodyStyle={{ padding: "18px 20px" }}
          style={{ borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 24 }}
        >
          <p style={{ margin: "0 0 12px", color: "#262626", fontSize: 14, lineHeight: 1.55 }}>
            Choose a{" "}
            <Link to="/plans" className="no-underline" style={{ color: "#01ADF0", fontWeight: 600 }}>
              care plan
            </Link>{" "}
            to book your next appointment. Appointments booked without one are cancelled{" "}
            <strong style={{ color: "#FF4D4F" }}>48 hours</strong> before the start time.
          </p>
          <p style={{ margin: 0, color: "#262626", fontSize: 14, lineHeight: 1.55 }}>
            You can still book a free 15-minute call with our Care Guide.
          </p>
        </CardComponent>
      )}

      {!selectedGroup && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              color: "#335CAD",
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1,
              letterSpacing: "0.05em",
              margin: "0 0 20px 0",
            }}
          >
            Select which care provider you would like to book with
          </h1>
          <Tooltip title={providerInfo}>
            <InfoCircleOutlined
              style={{ color: "#335CAD", fontSize: 20, marginTop: 2, cursor: "help", flexShrink: 0 }}
              aria-label="More information"
            />
          </Tooltip>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "flex-start", padding: "48px 0" }}>
          <Spin size="large" />
        </div>
      ) : orderedGroups.length === 0 ? (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            color: "#94a3b8",
            border: "1px dashed #E2E8F0",
            borderRadius: 12,
          }}
        >
          <p style={{ margin: 0 }}>No care provider groups available at this time.</p>
        </div>
      ) : !selectedGroup ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            width: 488,
            maxWidth: "100%",
          }}
        >
          {orderedGroups.map((group) => {
            const theme = themeForGroupName(group.groupName);
            const bullets = Array.isArray(theme.bulletPoints) ? theme.bulletPoints : [];
            const cardTitle = theme.displayName ?? group.groupName;
            const rowAlign = bullets.length > 0 ? "flex-start" : "center";
            const cardBodyStyle = {
              fontFamily: '"Montserrat", sans-serif',
              fontSize: 14,
              lineHeight: 1.55,
              color: "#262626",
            };

            const isComingSoon = Boolean(theme.comingSoon) && Boolean(group.__careGuidePlaceholder);
            const cardKey = group.__careGuidePlaceholder
              ? "care-guide-coming-soon"
              : group.groupId ?? group.groupName;
            const cardSurfaceStyle = {
              width: "100%",
              textAlign: "left",
              borderRadius: 16,
              border: "none",
              background: theme.background,
              padding: "18px 20px",
              overflow: "hidden",
              boxSizing: "border-box",
            };

            const comingSoonBanner = isComingSoon ? (
              <div
                style={{
                  background: "rgba(0, 0, 0, 0.2)",
                  color: "#fff",
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.1em",
                  margin: "-18px -20px 14px -20px",
                  width: "calc(100% + 40px)",
                  minWidth: 0,
                  height: 80,
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                COMING SOON
              </div>
            ) : null;

            const cardMainRow = (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: rowAlign,
                  gap: 16,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: 28,
                    lineHeight: 1,
                    width: 44,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {theme.iconSrc ? (
                    <img
                      src={theme.iconSrc}
                      alt=""
                      style={{
                        width: 44,
                        height: 44,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  ) : (
                    theme.emoji
                  )}
                </span>
                <div style={{ flex: "1 1 auto", minWidth: 0, textAlign: "left" }}>
                  <div
                    style={{
                      fontFamily: '"Montserrat", sans-serif',
                      fontWeight: 700,
                      fontSize: 17,
                      color: "#0F172A",
                      marginBottom: bullets.length > 0 ? 8 : theme.subtitle ? 4 : 0,
                    }}
                  >
                    {cardTitle}
                  </div>
                  {bullets.length > 0 ? (
                    <ul
                      style={{
                        ...cardBodyStyle,
                        margin: 0,
                        paddingLeft: 18,
                        listStyleType: "disc",
                        fontWeight: 400,
                        color: "#1e3a5f",
                      }}
                    >
                      {bullets.map((line) => (
                        <li key={line} style={{ marginBottom: 4 }}>
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : theme.subtitle ? (
                    <div
                      style={{
                        ...cardBodyStyle,
                        fontStyle: "italic",
                        color: "#334155",
                      }}
                    >
                      {theme.subtitle}
                    </div>
                  ) : null}
                </div>
                {!isComingSoon ? (
                  <ArrowRightOutlined
                    style={{
                      fontSize: 18,
                      color: "#64748B",
                      flexShrink: 0,
                      marginTop: bullets.length > 0 ? 2 : 0,
                    }}
                    aria-hidden
                  />
                ) : null}
              </div>
            );

            const cardBody = (
              <>
                {comingSoonBanner}
                {isComingSoon ? (
                  <div
                    style={{
                      background: theme.comingSoonBodyBg ?? "#FEF3E2",
                      padding: "18px 20px 22px",
                    }}
                  >
                    {cardMainRow}
                  </div>
                ) : (
                  cardMainRow
                )}
              </>
            );

            return (
              <React.Fragment key={cardKey}>
                {isComingSoon ? (
                  <div
                    style={{ ...cardSurfaceStyle, cursor: "default" }}
                    aria-label={`${cardTitle}. Coming soon.`}
                  >
                    {cardBody}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleBooking(group)}
                    style={{
                      ...cardSurfaceStyle,
                      cursor: "pointer",
                      transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = "scale(0.99)";
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = "";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                    }}
                  >
                    {cardBody}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={handleBackToCategories}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              marginBottom: 20,
              color: "#335CAD",
              fontFamily: '"Montserrat", sans-serif',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <ArrowLeftOutlined style={{ fontSize: 13 }} />
            Choose who you would like to book with
          </button>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                color: selectedTheme?.accent || "#335CAD",
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: "0.05em",
                margin: 0,
              }}
            >
              {selectedGroupTitle}
            </h2>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginLeft: "auto",
              }}
            >
              {selectedCreditKey && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 18px",
                    borderRadius: 999,
                    background: "#f0f4fa",
                    border: "1px solid #2563eb",
                    fontFamily: '"Montserrat", sans-serif',
                  }}
                >
                  {credits?.loading ? (
                    <span style={{ fontSize: 15, color: "#595959" }}>Loading credits…</span>
                  ) : credits?.error ? (
                    <span style={{ fontSize: 15, color: "#595959" }}>Credits unavailable</span>
                  ) : (
                    <>
                      <span
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                          color: selectedCreditsRemaining === 0 ? "#FF4D4F" : "#2563eb",
                        }}
                      >
                        {selectedCreditsRemaining}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                          credits remaining
                        </span>
                        <span style={{ fontSize: 12, color: "#595959" }}>
                          1 credit per appointment
                        </span>
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div >
          {loadingDetails ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "36px 16px",
              }}
            >
              <Spin size="large" />
            </div>
          ) : visibleCaregiverDetails.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 14,
                margin: "24px 0",
                fontFamily: '"Montserrat", sans-serif',
              }}
            >
              No caregiver details available
            </p>
          ) : filteredCaregiverDetails.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 14,
                margin: "24px 0",
                fontFamily: '"Montserrat", sans-serif',
              }}
            >
              No caregivers match the selected filters.
            </p>
          ) : (
            <div style={{ borderRadius: 10, overflow: "hidden", background: "#fff" }}>
              {filteredCaregiverDetails.map((caregiver, index) => {
                const slotLocations = Array.from(
                  new Set(
                    (caregiver.slots || [])
                      .map((s) => addressForLocation(s.location))
                      .filter(Boolean),
                  ),
                );
                const isLast = index === filteredCaregiverDetails.length - 1;

                const rowStyle = {
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                  padding: "20px 16px",
                  textAlign: "left",
                  width: "100%",
                  background: "#fff",
                  border: "none",
                  borderBottom: isLast ? "none" : "1px solid #e8e8e8",
                };

                const rowContent = (
                  <>
                    <img
                      src={caregiver.picture || neutralDoctorAvatar}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = neutralDoctorAvatar;
                      }}
                      style={{
                        width: 102,
                        height: 102,
                        borderRadius: 8,
                        objectFit: "cover",
                        flexShrink: 0,
                        background: "#e2e8f0",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3
                        style={{
                          fontFamily: '"Montserrat", sans-serif',
                          fontSize: 16,
                          fontWeight: 700,
                          color: "rgb(51, 92, 173)",
                          margin: "0 0 6px",
                          lineHeight: 1.3,
                        }}
                      >
                        {caregiver.name}
                        {caregiver.roleName ? ` (${caregiver.roleName})` : ""}
                        {!hasCreditFor(caregiver) && (
                          <Tag color="red" style={{ marginLeft: 8 }}>
                            No credits
                          </Tag>
                        )}
                      </h3>

                      {!!caregiver.bio && (
                        <p
                          style={{
                            fontFamily: '"Montserrat", sans-serif',
                            fontSize: 14,
                            color: "#595959",
                            lineHeight: 1.55,
                            margin: "0 0 10px",
                          }}
                        >
                          {caregiver.bio}
                        </p>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: slotLocations.length > 1 ? "flex-start" : "center",
                          gap: 8,
                          fontFamily: '"Montserrat", sans-serif',
                          fontSize: 13,
                          color: "#434343",
                        }}
                      >
                        <EnvironmentOutlined
                          style={{
                            color: "#01ADF0",
                            fontSize: 15,
                            marginTop: slotLocations.length > 1 ? 2 : 0,
                          }}
                          aria-hidden
                        />
                        {slotLocations.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {slotLocations.map((loc) => (
                              <span key={loc}>{loc}</span>
                            ))}
                          </div>
                        ) : (
                          <span>Book via Zoom Scheduler</span>
                        )}
                      </div>
                    </div>

                    <RightOutlined
                      style={{
                        color: "#01ADF0",
                        fontSize: 18,
                        alignSelf: "center",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    />
                  </>
                );

                return (
                  <button
                    key={caregiver.userId || caregiver.userRef || index}
                    type="button"
                    onClick={() => handleSelectCaregiver(caregiver)}
                    style={{
                      ...rowStyle,
                      cursor: "pointer",
                      ...(hasCreditFor(caregiver) ? {} : { opacity: 0.55 }),
                    }}
                  >
                    {rowContent}
                  </button>
                );
              })}
            </div>
          )}
          </div>
        </div>
      )}

      <Modal
        open={infoModalOpen}
        onCancel={() => {
          setInfoModalOpen(false);
          setPendingGroup(null);
        }}
        footer={null}
        centered
        title={
          <span
            style={{
              color: "#335CAD",
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Appointment Information
          </span>
        }
      >
        <ul
          style={{
            paddingLeft: 18,
            marginBottom: 16,
            color: "#1e293b",
            fontFamily: '"Montserrat", sans-serif',
            fontSize: 14,
            lineHeight: 1.7,
          }}
        >
          <li>
            First <span style={{ color: "#DC2626", fontWeight: 700 }}>clinician</span> session is{" "}
            <strong>1 hour</strong> and recommended to be <strong>in-person (with exceptions)</strong>
          </li>
          <li>
            First <span style={{ color: "#DC2626", fontWeight: 700 }}>fertility coach</span> and{" "}
            <span style={{ color: "#DC2626", fontWeight: 700 }}>nutritionist</span> sessions are{" "}
            <strong>1 hour (virtual)</strong>
          </li>
          <li>
            All <strong>follow-ups</strong> are <strong>30 minutes</strong> (can be in-person or virtual)
          </li>
        </ul>
        <Checkbox
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.target.checked)}
          style={{ marginBottom: 16 }}
        >
          Got it, don&apos;t show again
        </Checkbox>
        <div style={{ textAlign: "right" }}>
          <Button type="primary" onClick={handleContinueFromInfoModal}>
            Continue
          </Button>
        </div>
      </Modal>

      <BookingModal
        open={!!bookingClinician}
        clinician={bookingClinician}
        group={selectedGroup}
        categoryId={selectedTheme?.categoryId}
        isCareGuide={isCareGuideSelected}
        noCredits={!!bookingClinician && !hasCreditFor(bookingClinician)}
        isFollowUp={isFollowUp}
        patient={{
          id: auth?.obj?.id,
          firstName: auth?.obj?.firstName,
          lastName: auth?.obj?.lastName,
          email: auth?.obj?.email,
        }}
        onCancel={() => setBookingClinician(null)}
      />
    </div>
  );
};

export default Services;
